import { useRef, useEffect, useState, FC } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader, GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';

interface IndividualModelViewerProps {
  initialModelPath?: string;
}

const IndividualModelViewer: FC<IndividualModelViewerProps> = () => {
  const { variant = 'ground', unitId } = useParams<{ variant: string, unitId: string }>();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const modelRef = useRef<THREE.Group | null>(null);
  const directionalLightRef = useRef<THREE.DirectionalLight | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const autoRotateRef = useRef<boolean>(true);
  const userInteractedRef = useRef<boolean>(false);
  const modelCenterRef = useRef<THREE.Vector3 | null>(null);
  const initialCameraPositionRef = useRef<THREE.Vector3 | null>(null);
  const initialTargetRef = useRef<THREE.Vector3 | null>(null);
  const modelBoundsRef = useRef<THREE.Box3 | null>(null);
  const modelSizeRef = useRef<number>(1);
  const groundPlaneRef = useRef<THREE.Mesh | null>(null);
  const buttonClickTimeRef = useRef<number>(0);
  const modelLoadingAttemptRef = useRef<number>(0);
  const modelLoaderRef = useRef<GLTFLoader | null>(null);
  const dracoLoaderRef = useRef<DRACOLoader | null>(null);
  const interiorLightRef = useRef<THREE.PointLight | null>(null);
  const interiorLight2Ref = useRef<THREE.PointLight | null>(null);
  const interiorLight3Ref = useRef<THREE.PointLight | null>(null);
  const studioLightsRef = useRef<THREE.Group | null>(null);
  const fillLightRef = useRef<THREE.DirectionalLight | null>(null);
  const cameraOrbitRadiusRef = useRef<number>(12);
  const cameraOrbitHeightRef = useRef<number>(5);
  const isMountedRef = useRef<boolean>(true);
  const initialLoadTimeoutRef = useRef<number | null>(null);
  const warmLightRef = useRef<THREE.PointLight | null>(null);
  const devicePerformanceRef = useRef<'high'|'medium'|'low'>('high');

  
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [showMobileHelp, setShowMobileHelp] = useState(false);

  // const isSpecialUnit = (unit: string): boolean => {
  //   const specialUnits = ['56', '57', '65', '66', '8'];
  //   return specialUnits.includes(unit);
  // };

  const [isMobileDevice, setIsMobileDevice] = useState(() => {
    if (typeof navigator === 'undefined') return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  });
  
  // Updated model path logic for Ground/First floor models
  const getUnitModelPath = (unitNumber: string, variantType: string): string => {
    // Only applies to units 1-17
    const unitNum = parseInt(unitNumber, 10);
    const formattedUnitNum = unitNum < 10 ? `0${unitNum}` : `${unitNum}`;
    
    if (unitNum >= 1 && unitNum <= 17) {
      const folder = `Unit_${formattedUnitNum}`;
      return `/assets/${folder}/${variantType === 'ground' ? 'Ground' : 'First'}.glb`;
    } else {
      // For units 18 and above, use the original scheme
      const folder = `Unit_${formattedUnitNum}`;
      return `/assets/${folder}/${variantType === 'ground' ? 'Without_Mezzanine' : 'Mezzanine'}.glb`;
    }
  };
  
  const getModelPath = () => {
    if (!unitId) {
      return variant === 'ground' ? '/assets/Unit_01/Ground.glb' : '/assets/Unit_01/First.glb';
    }
    return getUnitModelPath(unitId, variant);
  };

  const getFallbackPath = (attemptedUnitId: string, attemptedVariant: string): string => {
    const unitNum = parseInt(attemptedUnitId, 10);
    if (attemptedUnitId !== '1') {
      return variant === 'ground' ? '/assets/Unit_01/Ground.glb' : '/assets/Unit_01/First.glb';
    }
    
    // Fallback to the opposite model of the same unit
    if (unitNum >= 1 && unitNum <= 17) {
      return `/assets/Unit_${attemptedUnitId === '1' ? '01' : attemptedUnitId}/${attemptedVariant === 'ground' ? 'First' : 'Ground'}.glb`;
    } else {
      return `/assets/Unit_${attemptedUnitId === '1' ? '01' : attemptedUnitId}/${attemptedVariant === 'ground' ? 'Mezzanine' : 'Without_Mezzanine'}.glb`;
    }
  };

  const [currentModelPath, setCurrentModelPath] = useState<string>(getModelPath());
  const [activeModelButton, setActiveModelButton] = useState<string>(
    variant === 'ground' ? 'ground' : 'first'
  );

  useEffect(() => {
    setCurrentModelPath(getModelPath());
    setActiveModelButton(variant === 'ground' ? 'ground' : 'first');
    setLoadingError(null);
    setIsLoading(true);
    setLoadingProgress(0);
    modelLoadingAttemptRef.current = 0;
  }, [variant, unitId]);

  const setupGroundPlane = (model: THREE.Group) => {
    if (!sceneRef.current) return;
    if (groundPlaneRef.current && sceneRef.current.getObjectById(groundPlaneRef.current.id)) {
      sceneRef.current.remove(groundPlaneRef.current);
      groundPlaneRef.current = null;
    }
  
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const center = new THREE.Vector3();
    box.getCenter(center);
  
    const groundSize = Math.max(size.x, size.z) * 3;
    const groundGeometry = new THREE.PlaneGeometry(groundSize, groundSize);
    const groundMaterial = new THREE.ShadowMaterial({ opacity: 0.08, color: 0x000000 });
    const groundPlane = new THREE.Mesh(groundGeometry, groundMaterial);
    groundPlane.rotation.x = -Math.PI / 2;
  
    groundPlane.position.y = box.min.y - 0.01;
    
    groundPlane.position.x = 0;
    groundPlane.position.z = 0;
    
    // Reset the model's X and Z position to center it, preserve Y position for height
    model.position.x = -center.x;
    model.position.z = -center.z;
  
    groundPlane.receiveShadow = false;
    sceneRef.current.add(groundPlane);
    groundPlaneRef.current = groundPlane;
  };

  const changeModel = (_modelPath: string, buttonId: string, newVariant: string) => {
    const now = Date.now();
    if (now - buttonClickTimeRef.current < 500) return;
    if (activeModelButton === buttonId) return;
    buttonClickTimeRef.current = now;
    setIsLoading(true);
    setLoadingProgress(0);
    setLoadingError(null);
    modelLoadingAttemptRef.current = 0;
    navigate(`/individual/${newVariant}/${unitId}`);
  };

  const getThumbnailPath = (unitId: string, variantType: string) => {
    const formattedUnitNum = parseInt(unitId, 10) < 10 ? `${unitId}` : `${unitId}`;
    let suffix = '1';
    if (variantType === 'first') suffix = '2';
    else if (variantType === 'ground') suffix = '1';
    return `/thumbnails/${formattedUnitNum}-${suffix}.png`;
  };

  const initializeLoader = () => {
    if (!dracoLoaderRef.current) {
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath('https://unpkg.com/three@0.159.0/examples/jsm/libs/draco/');
      dracoLoader.setDecoderConfig({ type: 'js' });
      dracoLoaderRef.current = dracoLoader;
    }
    const ktx2Loader = new KTX2Loader();
    ktx2Loader.setTranscoderPath('https://unpkg.com/three@0.159.0/examples/jsm/libs/basis/');
    if (rendererRef.current) ktx2Loader.detectSupport(rendererRef.current);
    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoaderRef.current);
    loader.setKTX2Loader(ktx2Loader);
    loader.setMeshoptDecoder(MeshoptDecoder);
    loader.manager.setURLModifier((url) => `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`);
    return loader;
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    isMountedRef.current = true;
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    scene.background = new THREE.Color(0xfff8f5); // Warm white/cream background
    const camera = new THREE.PerspectiveCamera(
      isMobileDevice ? 57 : 40, // Slightly further increased FOV for mobile
      container.clientWidth / container.clientHeight,
      0.1,
      1000
    );
    cameraRef.current = camera;
    const renderer = new THREE.WebGLRenderer({
      antialias: !isMobileDevice || devicePerformanceRef.current !== 'low',
      powerPreference: isMobileDevice ? 'low-power' : 'high-performance',
      precision: isMobileDevice ? 'mediump' : 'highp',
      alpha: !isMobileDevice || devicePerformanceRef.current !== 'low',
      stencil: !isMobileDevice,
      depth: true
    });
    rendererRef.current = renderer;
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2.0));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.5;
    container.appendChild(renderer.domElement);
    const controls = new OrbitControls(camera, renderer.domElement);
    controlsRef.current = controls;
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.screenSpacePanning = true;
    controls.maxPolarAngle = Math.PI / 2.20;
    controls.minPolarAngle = 0;
    controls.minDistance = 0.5;
    controls.maxDistance = 20.0;
    controls.zoomSpeed = 1.8;
    controls.rotateSpeed = 0.7;
    controls.enableZoom = true;
    controls.panSpeed = 1.8;
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN
    };
    controls.addEventListener('start', () => {
      userInteractedRef.current = true;
      autoRotateRef.current = false;
    });
    const setupEnhancedLighting = () => {
      // Clear any existing lights first
      scene.children.forEach(child => {
        if (
          child instanceof THREE.AmbientLight ||
          child instanceof THREE.DirectionalLight ||
          child instanceof THREE.PointLight ||
          child instanceof THREE.HemisphereLight
        ) {
          scene.remove(child);
        }
      });
    
      // Common base lights for all variants
      const ambientLight = new THREE.AmbientLight(
        0xfff6ef, 
        isMobileDevice ? 0.65 : 0.50
      );
      scene.add(ambientLight);
    
      // Main directional warm light
      const mainLight = new THREE.DirectionalLight(
        0xffecd6, 
        isMobileDevice ? 1.05 : 0.75
      );
      mainLight.position.set(15, 20, 15);
      
      // Configure shadows consistently
      mainLight.castShadow = !isMobileDevice;
      
      if (!isMobileDevice) {
        mainLight.shadow.mapSize.width = 2048;
        mainLight.shadow.mapSize.height = 2048;
        mainLight.shadow.camera.near = 0.5;
        mainLight.shadow.camera.far = 100;
        mainLight.shadow.camera.left = -25;
        mainLight.shadow.camera.right = 25;
        mainLight.shadow.camera.top = 25;
        mainLight.shadow.camera.bottom = -25;
        mainLight.shadow.bias = -0.0001;
        mainLight.shadow.normalBias = 0.04;
        mainLight.shadow.radius = 3.5;
        mainLight.shadow.blurSamples = 16;
      }
      
      directionalLightRef.current = mainLight;
      scene.add(mainLight);
    
      // Gentle warm fill directional
      const fillLight = new THREE.DirectionalLight(
        0xfff3e8, 
        isMobileDevice ? 0.7 : 0.5
      );
      fillLight.position.set(-15, 10, -15);
      fillLight.castShadow = false;
      scene.add(fillLight);
      fillLightRef.current = fillLight;
    
      // Additional fill light from another angle
      const fillLight2 = new THREE.DirectionalLight(
        0xfff6ef, 
        isMobileDevice ? 0.6 : 0.4
      );
      fillLight2.position.set(15, 10, -15);
      fillLight2.castShadow = false;
      scene.add(fillLight2);
    
      // Light hemisphere
      const hemiLight = new THREE.HemisphereLight(
        0xfffbf6, 
        0xffecd3, 
        isMobileDevice ? 0.5 : 0.35
      );
      scene.add(hemiLight);
    
      // Interior ambient
      const interiorAmbient = new THREE.AmbientLight(
        0xfffbf6, 
        isMobileDevice ? 0.4 : 0.25
      );
      scene.add(interiorAmbient);
    
      // Central warm point light
      const warmPoint = new THREE.PointLight(
        0xffc69a, 
        isMobileDevice ? 0.8 : 0.55, 
        45
      );
      warmPoint.position.set(0, 5, 0);
      warmPoint.castShadow = false;
      scene.add(warmPoint);
      warmLightRef.current = warmPoint;
    
      // Interior lights - consistent regardless of variant
      const interiorLight = new THREE.PointLight(
        0xffefe3, 
        isMobileDevice ? 1.0 : 0.7, 
        45
      );
      interiorLight.position.set(0, 5, 0);
      interiorLight.castShadow = false;
      scene.add(interiorLight);
      interiorLightRef.current = interiorLight;
    
      const interiorLight2 = new THREE.PointLight(
        0xfff3e8, 
        isMobileDevice ? 0.7 : 0.5, 
        40
      );
      interiorLight2.position.set(0, 2, -5);
      interiorLight2.castShadow = false;
      scene.add(interiorLight2);
      interiorLight2Ref.current = interiorLight2;
    
      const interiorLight3 = new THREE.PointLight(
        0xffead0, 
        isMobileDevice ? 0.5 : 0.35, 
        35
      );
      interiorLight3.position.set(0, 3, 5);
      interiorLight3.castShadow = false;
      scene.add(interiorLight3);
      interiorLight3Ref.current = interiorLight3;
    
      // Warm accents on inner walls
      const innerWallLight1 = new THREE.PointLight(
        0xffead0, 
        isMobileDevice ? 0.5 : 0.35, 
        20
      );
      innerWallLight1.position.set(-3, 3, 0);
      scene.add(innerWallLight1);
    
      const innerWallLight2 = new THREE.PointLight(
        0xffead0, 
        isMobileDevice ? 0.5 : 0.35, 
        20
      );
      innerWallLight2.position.set(3, 3, 0);
      scene.add(innerWallLight2);
    
      const innerWallLight3 = new THREE.PointLight(
        0xffead0, 
        isMobileDevice ? 0.5 : 0.35, 
        20
      );
      innerWallLight3.position.set(0, 3, -3);
      scene.add(innerWallLight3);
    };
    
    setupEnhancedLighting();

    const setupMouseEvents = () => {
      const onUserInteraction = () => {
        userInteractedRef.current = true;
        autoRotateRef.current = false;
      };
      container.addEventListener('mousedown', onUserInteraction);
      container.addEventListener('wheel', onUserInteraction);
      container.addEventListener('touchstart', onUserInteraction);
      return () => {
        container.removeEventListener('mousedown', onUserInteraction);
        container.removeEventListener('wheel', onUserInteraction);
        container.removeEventListener('touchstart', onUserInteraction);
      };
    };
    const cleanupMouseEvents = setupMouseEvents();

    if (initialLoadTimeoutRef.current) clearTimeout(initialLoadTimeoutRef.current);

    initialLoadTimeoutRef.current = window.setTimeout(() => {
      if (!isMountedRef.current) return;
      modelLoaderRef.current = initializeLoader();
      loadModel();
      initialLoadTimeoutRef.current = null;
    }, 100);

    let introAnimation = true;
    let animationStartTime = 0;
    const animationDuration = 1500;

    const loadModel = () => {
      if (!isMountedRef.current) return;
      if (!modelLoaderRef.current) {
        setLoadingError("Error initializing model loader");
        setIsLoading(false);
        return;
      }
      if (!currentModelPath) {
        setLoadingError("No model path specified");
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setLoadingError(null);
      modelLoadingAttemptRef.current += 1;

      try {
        const dracoLoader = dracoLoaderRef.current;
        if (!dracoLoader) throw new Error("DRACO loader not initialized");
        const loader = new GLTFLoader();
        loader.setDRACOLoader(dracoLoader);
        if (rendererRef.current) {
          const ktx2Loader = new KTX2Loader();
          ktx2Loader.setTranscoderPath('https://unpkg.com/three@0.159.0/examples/jsm/libs/basis/');
          ktx2Loader.detectSupport(rendererRef.current);
          loader.setKTX2Loader(ktx2Loader);
        }
        loader.setMeshoptDecoder(MeshoptDecoder);
        loader.manager.setURLModifier((url) => {
          if (url.includes('glb')) return `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`;
          return url;
        });

        loader.load(
          currentModelPath,
          (gltf: GLTF) => {
            if (!isMountedRef.current) return;
            if (modelRef.current && sceneRef.current) {
              sceneRef.current.remove(modelRef.current);
              modelRef.current = null;
            }
            const model = gltf.scene;
            modelRef.current = model;
            const box = new THREE.Box3().setFromObject(model);
            modelBoundsRef.current = box.clone();
            const center = new THREE.Vector3();
            box.getCenter(center);
            modelCenterRef.current = center.clone();
            const size = box.getSize(new THREE.Vector3());
            const modelDiagonal = size.length();
            modelSizeRef.current = modelDiagonal;
            model.position.set(0, 0, 0);
            const newBox = new THREE.Box3().setFromObject(model);
            const newCenter = new THREE.Vector3();
            newBox.getCenter(newCenter);
            model.position.sub(newCenter);
            const baseScale = 15 / modelDiagonal;
            model.scale.set(baseScale, baseScale, baseScale);
            const finalBox = new THREE.Box3().setFromObject(model);
            finalBox.getCenter(modelCenterRef.current);
            modelBoundsRef.current = finalBox.clone();
            model.rotation.y = 0;
            const getUnitRotation = () => {
              if (!unitId) return 0;
              const unit = parseInt(unitId, 10);
              if ((unit >= 28 && unit <= 36) || (unit >= 47 && unit <= 54)) return Math.PI * 2;
              return 0;
            }
            const unitRotation = getUnitRotation();
            if (unitRotation !== 0) model.rotation.y = unitRotation;

            model.traverse((child: THREE.Object3D) => {
              if ((child as THREE.Mesh).isMesh) {
                const mesh = child as THREE.Mesh;
                
                mesh.castShadow = true;
                mesh.receiveShadow = !isMobileDevice;
                
                const processMaterials = (material: THREE.Material) => {
                  if (!(material instanceof THREE.MeshStandardMaterial)) return;
                  const m = material as THREE.MeshStandardMaterial;
                  const name = (m.name || '').toLowerCase();
                
                  const isRollerDoorOrStructural =
                    (
                      name &&
                      (
                        name.includes('roller') ||
                        name.includes('door') ||
                        name.includes('gate') ||
                        name.includes('metal') ||
                        name.includes('frame')
                      )
                    ) ||
                    (
                      m.color &&
                      m.color.r < 0.2 &&
                      m.color.g < 0.2 &&
                      m.color.b < 0.2
                    );
                
                  // baseline: ensure shadows render correctly and remove emissive glow
                  m.shadowSide = THREE.FrontSide;
                  if (m.emissive) {
                    // remove any emissive highlight
                    if (typeof (m.emissive as any).setScalar === 'function') {
                      (m.emissive as any).setScalar(0);
                    } else {
                      m.emissive.set(0, 0, 0);
                    }
                  }
              
                  if (isRollerDoorOrStructural) {
                    m.roughness = Math.max(m.roughness, 0.6);
                    m.metalness = Math.min(m.metalness, 0.3);
                    m.envMapIntensity = 0.5;
                    return;
                  }
                
                  m.roughness = Math.max(m.roughness, 0.7);
                  m.metalness = Math.min(m.metalness, 0.05);
                  m.envMapIntensity = 0.6;
                };
              
                if (Array.isArray(mesh.material)) mesh.material.forEach(processMaterials);
                else if (mesh.material) processMaterials(mesh.material);
              }
            });

            if (rendererRef.current) {
              rendererRef.current.shadowMap.enabled = true;
              rendererRef.current.shadowMap.type = THREE.PCFSoftShadowMap;
            }
            
            setupGroundPlane(model);
            
            const boundingBox = new THREE.Box3().setFromObject(model);
            const boundingSphere = new THREE.Sphere();
            boundingBox.getBoundingSphere(boundingSphere);
            // Increased camera distance multiplier for mobile to zoom out more
            const cameraDistanceMultiplier = windowWidth <= 768 ? 8.5 : 2.2; // Slightly further increased from 7.5 to 8.5 for mobile
            const cameraOrbitRadius = boundingSphere.radius * cameraDistanceMultiplier;
            const cameraHeightMultiplier = windowWidth <= 768 ? 1.0 : 0.7;
            const cameraOrbitHeight = boundingSphere.center.y + boundingSphere.radius * cameraHeightMultiplier;
                        
            cameraOrbitRadiusRef.current = cameraOrbitRadius;
            cameraOrbitHeightRef.current = cameraOrbitHeight;
            const centerY = windowWidth <= 768 ? boundingSphere.center.y : boundingSphere.center.y;
            const centerX = windowWidth <= 768 ? 0 : boundingSphere.center.x;
            const centerZ = windowWidth <= 768 ? 0 : boundingSphere.center.z;
            
            if (interiorLightRef.current) {
              interiorLightRef.current.intensity = 1.4;
              interiorLightRef.current.color.set(0xffffff);
              interiorLightRef.current.position.set(
                centerX + (boundingSphere.radius * 0.2), // Offset from center
                centerY + (boundingSphere.radius * 0.5),
                centerZ
              );
              
              if (interiorLight2Ref.current) {
                interiorLight2Ref.current.intensity = 1.2;
                interiorLight2Ref.current.color.set(0xffffff);
                interiorLight2Ref.current.position.set(
                  centerX - (boundingSphere.radius * 0.2), // Offset to opposite side
                  centerY + (boundingSphere.radius * 0.25),
                  centerZ + (boundingSphere.radius * 0.4)
                );
              }
              
              if (interiorLight3Ref.current) {
                interiorLight3Ref.current.intensity = 0.8; // Further reduced
                interiorLight3Ref.current.color.set(0xffffff);
                interiorLight3Ref.current.position.set(
                  centerX,
                  centerY + (boundingSphere.radius * 0.3),
                  centerZ - (boundingSphere.radius * 0.35)
                );
              }
            }
            
            // Position the warm light for unit 27
            if (unitId === '27' && warmLightRef.current) {
              warmLightRef.current.position.set(
                centerX,
                centerY + (boundingSphere.radius * 0.6),
                centerZ
              );
              warmLightRef.current.distance = boundingSphere.radius * 2.5;
            }
            
            const centerVec = boundingSphere.center.clone();
            const angleStart = -Math.PI / 4;
            const angleEnd = 0;
            camera.position.set(
              centerVec.x + cameraOrbitRadius * Math.sin(angleStart),
              cameraOrbitHeight,
              centerVec.z + cameraOrbitRadius * Math.cos(angleStart)
            );
            camera.lookAt(centerVec);
            controls.target.copy(centerVec);
            controls.update();
            initialCameraPositionRef.current = camera.position.clone();
            initialTargetRef.current = centerVec.clone();
            if (directionalLightRef.current) {
              const light = directionalLightRef.current;
              light.position.set(
                boundingSphere.center.x + boundingSphere.radius * 1.5,
                boundingSphere.center.y + boundingSphere.radius * 2.2,
                boundingSphere.center.z + boundingSphere.radius * 1.5
              );
              light.intensity = 1.8;
              
              // Enable shadows
              light.castShadow = true;
              
              if (fillLightRef.current) {
                fillLightRef.current.position.set(
                  boundingSphere.center.x - boundingSphere.radius * 1.0,
                  boundingSphere.center.y + boundingSphere.radius * 1.8,
                  boundingSphere.center.z - boundingSphere.radius * 1.0
                );
                fillLightRef.current.intensity = 1.15;
              }
              const targetObject = new THREE.Object3D();
              targetObject.position.copy(boundingSphere.center);
              scene.add(targetObject);
              light.target = targetObject;
            }

            scene.add(model);
            setIsLoading(false);
            setLoadingError(null);
            introAnimation = true;
            animationStartTime = Date.now();

            const animate = () => {
              if (!isMountedRef.current) return;
              animationFrameRef.current = requestAnimationFrame(animate);
              if (introAnimation) {
                const elapsed = Date.now() - animationStartTime;
                const progress = Math.min(elapsed / animationDuration, 1);
                const t = progress < 0.5
                  ? 2 * progress * progress
                  : -1 + (4 - 2 * progress) * progress;
                const angle = angleStart + (angleEnd - angleStart) * t;
                camera.position.set(
                  centerVec.x + cameraOrbitRadius * Math.sin(angle),
                  cameraOrbitHeight,
                  centerVec.z + cameraOrbitRadius * Math.cos(angle)
                );
                controls.target.lerp(centerVec, t);
                camera.lookAt(centerVec);
                if (progress >= 1) {
                  introAnimation = false;
                  controls.enabled = true;
                  initialCameraPositionRef.current = camera.position.clone();
                  autoRotateRef.current = true;
                } else {
                  controls.enabled = true;
                }
              } else if (model && autoRotateRef.current && !userInteractedRef.current) {
                const slowOrbitSpeed = 0.00012;
                const angle =
                  ((Date.now() - animationStartTime) * slowOrbitSpeed) % (2 * Math.PI);
                camera.position.x = centerVec.x + cameraOrbitRadius * Math.sin(angle);
                camera.position.z = centerVec.z + cameraOrbitRadius * Math.cos(angle);
                camera.position.y = cameraOrbitHeight;
                camera.lookAt(centerVec);
              }
              controls.update();
              renderer.render(scene, camera);
            };

            if (animationFrameRef.current !== null) cancelAnimationFrame(animationFrameRef.current);
            animate();

            const handleDoubleClick = () => {
              if (initialCameraPositionRef.current && initialTargetRef.current) {
                const controls = controlsRef.current;
                const camera = cameraRef.current;
                if (!controls || !camera) return;
                const startPosition = camera.position.clone();
                const startTarget = controls.target.clone();
                const endPosition = initialCameraPositionRef.current.clone();
                const endTarget = initialTargetRef.current.clone();
                const duration = 1000;
                const startTime = Date.now();
                const animateReset = () => {
                  const now = Date.now();
                  const elapsed = now - startTime;
                  const progress = Math.min(elapsed / duration, 1);
                  const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
                  const easedProgress = easeOut(progress);
                  camera.position.lerpVectors(startPosition, endPosition, easedProgress);
                  controls.target.lerpVectors(startTarget, endTarget, easedProgress);
                  controls.update();
                  if (progress < 1) requestAnimationFrame(animateReset);
                };
                animateReset();
              }
            };
            container.addEventListener('dblclick', handleDoubleClick);

            const getMinTargetY = () =>
              groundPlaneRef.current ? groundPlaneRef.current.position.y : -0.01;
            controls.addEventListener('change', () => {
              const minTargetY = getMinTargetY();
              if (controls.target.y < minTargetY) {
                controls.target.y = minTargetY;
                controls.update();
              }
            });

            return () => {
              container.removeEventListener('dblclick', handleDoubleClick);
            };
          },
          (xhr: ProgressEvent<EventTarget>) => {
            if (!isMountedRef.current) return;
            if (xhr.total) {
              const progress = Math.min(Math.floor((xhr.loaded / xhr.total) * 100), 100);
              setLoadingProgress(progress);
            }
          },
          (error: any) => {
            if (!isMountedRef.current) return;
            setLoadingError(`Failed to load model. ${error.message || ''}`);
            if (modelLoadingAttemptRef.current <= 2) {
              let fallbackPath = getFallbackPath(unitId || '1', variant);
              if (fallbackPath === currentModelPath) fallbackPath = '/assets/Unit_01/Ground.glb';
              setCurrentModelPath(fallbackPath);
              setTimeout(() => {
                if (isMountedRef.current) loadModel();
              }, 500);
            } else {
              setIsLoading(false);
              setLoadingError("Failed to load model after multiple attempts. Please try a different unit or variant.");
            }
          }
        );
      } catch (err: any) {
        setLoadingError(`Error loading model: ${err.message || 'Unknown error'}`);
        setIsLoading(false);
      }
    };

    let lastFrameTime = 0;
    const targetFPS = 24;
    const frameInterval = 1000 / targetFPS;
    const animatePreload = (currentTime: number) => {
      if (!isMountedRef.current) return;
      if (isLoading) {
        animationFrameRef.current = requestAnimationFrame(animatePreload);
        const deltaTime = currentTime - lastFrameTime;
        if (deltaTime < frameInterval) return;
        lastFrameTime = currentTime - (deltaTime % frameInterval);
        if (controlsRef.current) controlsRef.current.update();
        if (rendererRef.current && sceneRef.current && cameraRef.current) {
          rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
      }
    };
    animationFrameRef.current = requestAnimationFrame(animatePreload);

    let resizeTimeout: number;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => {
        if (!container || !isMountedRef.current) return;
        const width = container.clientWidth;
        const height = container.clientHeight;
        if (cameraRef.current) {
          cameraRef.current.aspect = width / height;
          cameraRef.current.updateProjectionMatrix();
        }
        if (rendererRef.current) {
          rendererRef.current.setSize(width, height);
        }
      }, 100);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      isMountedRef.current = false;
      if (initialLoadTimeoutRef.current) {
        clearTimeout(initialLoadTimeoutRef.current);
        initialLoadTimeoutRef.current = null;
      }
      if (cleanupMouseEvents) cleanupMouseEvents();
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (controlsRef.current) {
        controlsRef.current.dispose();
        controlsRef.current = null;
      }
      if (rendererRef.current) {
        rendererRef.current.dispose();
        rendererRef.current.forceContextLoss();
        if (container.contains(rendererRef.current.domElement)) {
          container.removeChild(rendererRef.current.domElement);
        }
        rendererRef.current = null;
      }
      if (sceneRef.current) {
        sceneRef.current.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            if (Array.isArray(child.material)) {
              child.material.forEach((material) => {
                if (material.map) material.map.dispose();
                material.dispose();
              });
            } else if (child.material) {
              if (child.material.map) child.material.map.dispose();
              child.material.dispose();
            }
            if (child.geometry) child.geometry.dispose();
          }
        });
        sceneRef.current.clear();
        sceneRef.current = null;
      }
      if (groundPlaneRef.current) groundPlaneRef.current = null;
      if (dracoLoaderRef.current) {
        dracoLoaderRef.current.dispose();
        dracoLoaderRef.current = null;
      }
      if (modelLoaderRef.current) {
        if (modelLoaderRef.current.dracoLoader) {
          modelLoaderRef.current.dracoLoader.dispose();
        }
        modelLoaderRef.current = null;
      }
      if (interiorLightRef.current) interiorLightRef.current = null;
      if (interiorLight2Ref.current) interiorLight2Ref.current = null;
      if (interiorLight3Ref.current) interiorLight3Ref.current = null;
      if (studioLightsRef.current) studioLightsRef.current = null;
      if (fillLightRef.current) fillLightRef.current = null;
      if (warmLightRef.current) warmLightRef.current = null;
      modelRef.current = null;
      directionalLightRef.current = null;
      cameraRef.current = null;
      modelCenterRef.current = null;
      initialCameraPositionRef.current = null;
      initialTargetRef.current = null;
      modelBoundsRef.current = null;
    };
  }, [currentModelPath]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
  
    const onResize = () => setWindowWidth(window.innerWidth);
  
    const updateIsMobile = () => {
      const uaMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
      // consider touch capability as a secondary indicator
      const hasTouch =
        typeof navigator !== 'undefined' &&
        (('maxTouchPoints' in navigator && navigator.maxTouchPoints > 0) ||
          'ontouchstart' in window);
      setIsMobileDevice(uaMobile || hasTouch);
    };
  
    // run on mount to ensure correct initial value
    updateIsMobile();
  
    window.addEventListener('resize', onResize);
    window.addEventListener('resize', updateIsMobile);
    window.addEventListener('orientationchange', updateIsMobile);
  
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('resize', updateIsMobile);
      window.removeEventListener('orientationchange', updateIsMobile);
    };
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100vh',
        backgroundColor: '#fafafa',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Container for the 3D view */}
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          paddingBottom: windowWidth <= 768 ? '110px' : '0',
          padding: windowWidth <= 768 ? '0 10px' : '0',  // Add horizontal padding on mobile
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      />
  
      {/* Loading indicator (shared mobile + desktop) */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            zIndex: 10
          }}
        >
          <div className="custom-loader">
            <div className="circle-segment segment-1"></div>
            <div className="circle-segment segment-2"></div>
            <div className="circle-segment segment-3"></div>
            <div className="circle-segment segment-4"></div>
          </div>
  
          <p
            style={{
              color: '#333',
              fontSize: '16px',
              fontWeight: '500',
              marginTop: '25px',
              fontFamily: 'Arial, sans-serif'
            }}
          >
            Loading 3D Model... {loadingProgress}%
          </p>
  
          <div
            style={{
              width: '240px',
              height: '6px',
              backgroundColor: '#e0e0e0',
              borderRadius: '3px',
              overflow: 'hidden',
              marginTop: '8px'
            }}
          >
            <div
              style={{
                width: `${loadingProgress}%`,
                height: '100%',
                backgroundColor: '#e53935',
                borderRadius: '3px',
                transition: 'width 0.3s ease-in-out'
              }}
            />
          </div>
  
          {loadingError && (
            <p
              style={{
                color: '#e53935',
                fontSize: '14px',
                marginTop: '10px',
                fontFamily: 'Arial, sans-serif',
                maxWidth: '300px',
                textAlign: 'center'
              }}
            >
              {loadingError}
            </p>
          )}
  
          <style>
            {`
              .custom-loader {
                position: relative;
                width: 80px;
                height: 80px;
                animation: rotate 2s linear infinite;
              }
              .circle-segment {
                position: absolute;
                width: 45%;
                height: 45%;
                border-radius: 50%;
                background-color: #e53935;
              }
              .segment-1 { top: 0; left: 0; }
              .segment-2 { top: 0; right: 0; }
              .segment-3 { bottom: 0; right: 0; }
              .segment-4 { bottom: 0; left: 0; }
              @keyframes rotate {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      )}
  
      {/* Back button - desktop vs mobile sizes */}
      {!isLoading && (
        <button
          type="button"
          aria-label="Go back"
          onClick={() => navigate('/')}
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            zIndex: 20,
            width: windowWidth <= 768 ? '48px' : '56px',
            height: windowWidth <= 768 ? '48px' : '56px',
            padding: 0,
            cursor: 'pointer',
            border: '2px solid #555',
            borderRadius: '50%',
            backgroundColor: '#f5f5f5',
            fontSize: '14px',
            fontFamily: 'Arial, sans-serif',
            color: '#333333',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
          }}
        >
          <span style={{ fontSize: '26px', lineHeight: 1 }}>←</span>
        </button>
      )}
  
      {/* Desktop Model selection (left-bottom) OR Mobile centered thumbnails */}
      {!isLoading && (
        <>
          {windowWidth > 768 && !isMobileDevice ? (
            // Desktop: thumbnails bottom-left with two options (Ground/First)
            <div
              style={{
                position: 'absolute',
                bottom: '20px',
                left: '20px',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
              }}
            >
              {/* Ground Floor */}
              <button
                onClick={() => changeModel(getUnitModelPath(unitId || '1', 'ground'), 'ground', 'ground')}
                style={{
                  padding: 0,
                  cursor: 'pointer',
                  border: activeModelButton === 'ground' ? '2px solid #e53935' : '2px solid #aaaaaa',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  backgroundColor: 'transparent',
                  width: '120px',
                  height: '110px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <img
                  src={getThumbnailPath(unitId || '1', 'ground')}
                  alt="Ground Floor"
                  style={{ width: '100%', height: '75%', objectFit: 'cover' }}
                />
                <div
                  style={{
                    width: '100%',
                    backgroundColor: '#f5f5f5',
                    padding: '7px 0 4px 0',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontFamily: 'Arial, sans-serif',
                    color: '#333333',
                    fontWeight: 'bold'
                  }}
                >
                  Ground Floor
                </div>
              </button>
  
              {/* First Floor */}
              <button
                onClick={() => changeModel(getUnitModelPath(unitId || '1', 'first'), 'first', 'first')}
                style={{
                  padding: 0,
                  cursor: 'pointer',
                  border: activeModelButton === 'first' ? '2px solid #e53935' : '2px solid #aaaaaa',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  backgroundColor: 'transparent',
                  width: '120px',
                  height: '110px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <img
                  src={getThumbnailPath(unitId || '1', 'first')}
                  alt="First Floor"
                  style={{ width: '100%', height: '75%', objectFit: 'cover' }}
                />
                <div
                  style={{
                    width: '100%',
                    backgroundColor: '#f5f5f5',
                    padding: '7px 0 4px 0',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontFamily: 'Arial, sans-serif',
                    color: '#333333',
                    fontWeight: 'bold'
                  }}
                >
                  First Floor
                </div>
              </button>
            </div>
          ) : (
            (typeof window !== 'undefined' && windowWidth > window.innerHeight && isMobileDevice) ? (
              // Mobile Landscape: segmented control style at bottom
              <div
                style={{
                  position: 'absolute',
                  bottom: '40px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 'auto',
                  padding: 0
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    backgroundColor: '#E5E5E5',
                    borderRadius: '10px',
                    padding: '3px',
                    gap: 0,
                    minWidth: '240px',
                    width: 'auto'
                  }}
                >
                  {/* Ground Floor Button */}
                  <button
                    onClick={() => changeModel(getUnitModelPath(unitId || '1', 'ground'), 'ground', 'ground')}
                    style={{
                      display: 'block',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      margin: 0,
                      flex: 1,
                      minWidth: '0'
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        padding: '8px 12px',
                        borderRadius: activeModelButton === 'ground' ? '8px' : '0',
                        backgroundColor: activeModelButton === 'ground' ? '#FFFFFF' : 'transparent',
                        transition: 'all 0.2s ease-in-out',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxSizing: 'border-box'
                      }}
                    >
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: activeModelButton === 'ground' ? '600' : '400',
                          color: activeModelButton === 'ground' ? '#000000' : '#666666',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
                          textAlign: 'center',
                          userSelect: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Ground Floor
                      </span>
                    </div>
                  </button>
    
                  {/* First Floor Button */}
                  <button
                    onClick={() => changeModel(getUnitModelPath(unitId || '1', 'first'), 'first', 'first')}
                    style={{
                      display: 'block',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      margin: 0,
                      flex: 1,
                      minWidth: '0'
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        padding: '8px 12px',
                        borderRadius: activeModelButton === 'first' ? '8px' : '0',
                        backgroundColor: activeModelButton === 'first' ? '#FFFFFF' : 'transparent',
                        transition: 'all 0.2s ease-in-out',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxSizing: 'border-box'
                      }}
                    >
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: activeModelButton === 'first' ? '600' : '400',
                          color: activeModelButton === 'first' ? '#000000' : '#666666',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
                          textAlign: 'center',
                          userSelect: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        First Floor
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            ) : (
              // Mobile Portrait: centered at bottom (segmented control style)
              <div
                style={{
                  position: 'absolute',
                  bottom: (windowWidth > window.innerHeight && isMobileDevice ? '40px' : '180px'),
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 'auto',
                  padding: 0
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    backgroundColor: '#E5E5E5',
                    borderRadius: '10px',
                    padding: '3px',
                    gap: 0,
                    minWidth: '240px',
                    width: 'auto'
                  }}
                >
                  {/* Ground Floor Button */}
                  <button
                    onClick={() => changeModel(getUnitModelPath(unitId || '1', 'ground'), 'ground', 'ground')}
                    style={{
                      display: 'block',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      margin: 0,
                      flex: 1,
                      minWidth: '0'
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        padding: '8px 12px',
                        borderRadius: activeModelButton === 'ground' ? '8px' : '0',
                        backgroundColor: activeModelButton === 'ground' ? '#FFFFFF' : 'transparent',
                        transition: 'all 0.2s ease-in-out',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxSizing: 'border-box'
                      }}
                    >
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: activeModelButton === 'ground' ? '600' : '400',
                          color: activeModelButton === 'ground' ? '#000000' : '#666666',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
                          textAlign: 'center',
                          userSelect: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Ground Floor
                      </span>
                    </div>
                  </button>
    
                  {/* First Floor Button */}
                  <button
                    onClick={() => changeModel(getUnitModelPath(unitId || '1', 'first'), 'first', 'first')}
                    style={{
                      display: 'block',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      margin: 0,
                      flex: 1,
                      minWidth: '0'
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        padding: '8px 12px',
                        borderRadius: activeModelButton === 'first' ? '8px' : '0',
                        backgroundColor: activeModelButton === 'first' ? '#FFFFFF' : 'transparent',
                        transition: 'all 0.2s ease-in-out',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxSizing: 'border-box'
                      }}
                    >
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: activeModelButton === 'first' ? '600' : '400',
                          color: activeModelButton === 'first' ? '#000000' : '#666666',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
                          textAlign: 'center',
                          userSelect: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        First Floor
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            )
          )}
        </>
      )}
  
      {/* Unit Number Display with Dropdown Selector - desktop pill vs mobile responsive */}
      {!isLoading && (
        <div
          style={{
            position: 'absolute',
            top: (windowWidth > window.innerHeight && isMobileDevice && windowWidth <= 768) ? '20px' : (windowWidth <= 768 ? '80px' : '20px'),
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: windowWidth <= 768 ? (windowWidth <= 480 ? '6px 12px' : '6px 12px') : '8px 16px',
            backgroundColor: '#fff',
            borderRadius: '999px',
            boxShadow: '0 6px 18px rgba(22,28,37,0.12)',
            border: '1px solid rgba(0,0,0,0.06)',
            fontFamily: 'Roboto, Arial, sans-serif',
            fontSize: '15px',
            fontWeight: 600,
            color: '#111827',
            maxWidth: windowWidth <= 768 ? '85%' : '92%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            alignSelf: 'center'
          }}
        >
          <span
            style={{
              fontSize: windowWidth <= 480 ? '10px' : '12px',
              color: '#6b7280',
              fontWeight: 700,
              letterSpacing: '0.6px',
              textTransform: 'uppercase'
            }}
          >
            Unit
          </span>
  
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <select
              aria-label="Select unit"
              value={unitId || '1'}
              onChange={(e) => {
                const newUnitId = e.target.value;
                navigate(`/individual/${variant}/${newUnitId}`);
              }}
              style={{
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                padding: windowWidth <= 480 ? '6px 28px 6px 10px' : '8px 36px 8px 12px',
                minWidth: windowWidth <= 480 ? '60px' : '74px',
                borderRadius: '10px',
                border: '1px solid #e6e9ef',
                backgroundColor: '#fbfdff',
                fontSize: windowWidth <= 480 ? '14px' : '15px',
                fontWeight: 800,
                cursor: 'pointer',
                textAlign: 'center',
                color: '#0f172a',
                outline: 'none',
                boxShadow: 'none'
              }}
            >
              {Array.from({ length: 17 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
  
            <svg
              width={windowWidth <= 480 ? 14 : 16}
              height={windowWidth <= 480 ? 14 : 16}
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              style={{
                position: 'absolute',
                right: windowWidth <= 480 ? 8 : 10,
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                opacity: 0.8
              }}
            >
              <path
                d="M6 7l4 4 4-4"
                stroke="#374151"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
  
          {/* Divider */}
          <div
            style={{
              width: '1px',
              height: windowWidth <= 480 ? '18px' : '22px',
              backgroundColor: '#eef2f6',
              margin: windowWidth <= 480 ? '0 6px' : '0 8px',
              opacity: 0.95
            }}
            aria-hidden="true"
          />
  
          <div
            style={{
              fontSize: windowWidth <= 480 ? '12px' : '14px',
              color: '#374151',
              fontWeight: 700,
              maxWidth: windowWidth <= 480 ? '160px' : '420px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {variant === 'ground' ? 'Ground Floor' : 'First Floor'}
          </div>
        </div>
      )}
  
      {/* Controls help - Desktop (always visible) */}
      {!isLoading && windowWidth > 768 && !isMobileDevice && (
        <div
          style={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            padding: '10px 15px',
            color: '#333333',
            borderRadius: '5px',
            fontSize: '13px',
            fontFamily: 'Arial, sans-serif',
            fontWeight: '500',
            zIndex: 10,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 'bold', marginRight: '5px' }}>Left-click:</span> Rotate
          </span>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 'bold', marginRight: '5px' }}>Right-click:</span> Pan
          </span>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 'bold', marginRight: '5px' }}>Scroll:</span> Zoom
          </span>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 'bold', marginRight: '5px' }}>Double-click:</span> Reset View
          </span>
        </div>
      )}
  
      {/* Controls help - Mobile: icon that expands */}
      {!isLoading && isMobileDevice && (
        <div
          style={{
            position: 'absolute',
            bottom: (windowWidth > window.innerHeight ? '120px' : '200px'),
            right: '20px',
            zIndex: 11,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end'
          }}
        >
          <button
            onClick={() => setShowMobileHelp((s) => !s)}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.8)',
              border: '1px solid #ddd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
              cursor: 'pointer'
            }}
          >
            <span style={{ fontSize: '20px' }}>?</span>
          </button>
  
          {showMobileHelp && (
            <div
              style={{
                flexDirection: 'column',
                backgroundColor: 'rgba(255,255,255,0.95)',
                borderRadius: '8px',
                padding: '12px',
                marginTop: '10px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                border: '1px solid #eee',
                maxWidth: '200px',
                display: 'flex'
              }}
            >
              <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                <strong>One finger:</strong> Rotate
              </div>
              <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                <strong>Two fingers:</strong> Pan
              </div>
              <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                <strong>Pinch:</strong> Zoom
              </div>
              <div style={{ fontSize: '12px' }}>
                <strong>Double-tap:</strong> Reset View
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IndividualModelViewer;